/**
 * Toast Container Component
 * Displays toast notifications
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast, Toast } from '../lib/toast';

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  const getToastColor = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'from-green-600 to-green-700';
      case 'error':
        return 'from-red-600 to-red-700';
      case 'warning':
        return 'from-yellow-600 to-yellow-700';
      case 'info':
      default:
        return 'from-blue-600 to-blue-700';
    }
  };

  const getToastIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className={`min-w-[300px] max-w-md p-4 rounded-lg shadow-2xl bg-gradient-to-r ${getToastColor(
              toast.type
            )} border-2 border-white/20`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                {getToastIcon(toast.type)}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-semibold">{toast.message}</p>
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
